import { Button, ToggleSwitch } from '../atoms';
import { useErrorHandlerContext, useOpeningElementsControllerContext, useUserLanguage } from '@/customHooks';
import { useEffect, useMemo, useState } from 'react';
import { useGetRedsysConfigurationQuery, useSaveRedsysConfigurationMutation } from '@/graphql/generated';
import { NormalFormPasswordField, NormalFormTextField } from '../molecules';

const textsData = {
  en: {
    title: 'Redsys Configuration',
    enableToggleLabel: 'Enable Redsys for payments',
    field1: 'Terminal',
    field2: 'Merchant Code',
    field3: 'Merchant Key',
    submitButton: 'Save',
    success: {
      title: 'Redsys configuration saved',
      description: 'The Redsys configuration has been successfully saved.',
    }
  },
  es: {
    title: 'Configuración de Redsys',
    enableToggleLabel: 'Habilitar Redsys para pagos',
    field1: 'Terminal',
    field2: 'Código de Comercio',
    field3: 'Clave de Comercio',
    submitButton: 'Guardar',
    success: {
      title: 'Configuración de Redsys guardada',
      description: 'La configuración de Redsys se ha guardado correctamente.',
    }
  }
}

export const ResdysConfigurationForm = () => {
  const userLanguage = useUserLanguage()
  const texts = userLanguage === 'es' ? textsData.es : textsData.en

  const [isFormVisible, setIsFormVisible] = useState(false)

  const [redsysFormData, setRedsysFormData] = useState({
    terminal: '',
    merchantCode: '',
    merchantKey: '',
  })

  const [initialRedsysFormData, setInitialRedsysFormData] = useState({
    terminal: '',
    merchantCode: '',
    merchantKey: '',
  })

  const { data, loading: redsysConfigLoading } = useGetRedsysConfigurationQuery()
  const [saveRedsysConfiguration, { loading: saveRedsysConfigurationLoading }] =
    useSaveRedsysConfigurationMutation()

  const { confirmDialogModalOpen } = useOpeningElementsControllerContext()
  const { open: errorHandle } = useErrorHandlerContext()

  useEffect(() => {
    if (!data) return

    const redsysConfig = data.getRedsysConfiguration

    if (redsysConfig.__typename === 'RedsysUserConfigOutput') {
      const initialData = {
        terminal: redsysConfig.terminal,
        merchantCode: redsysConfig.merchantCode,
        merchantKey: '123456789abcdefghijk',
      }
      setRedsysFormData(initialData)
      setInitialRedsysFormData(initialData)
      setIsFormVisible(true)
    } else if (redsysConfig.__typename === 'ActionResponseMessageOutput') {
      setIsFormVisible(false)
    }
  }, [data, redsysConfigLoading])

  const onRedsysFieldChange = (
    field: 'terminal' | 'merchantCode' | 'merchantKey',
    value: string | null
  ) => {
    setRedsysFormData((prev) => ({
      ...prev,
      [field]: value ?? '',
    }))
  }

  const onSubmitRedsysConfig = () => {
    saveRedsysConfiguration({
      variables: {
        data: {
          terminal: redsysFormData.terminal,
          merchantCode: redsysFormData.merchantCode,
          merchantKey: redsysFormData.merchantKey,
        },
      },
    })
      .then(() => {
        setInitialRedsysFormData({ ...redsysFormData })

        confirmDialogModalOpen({
          title: texts.success.title,
          description: texts.success.description,
        })
      })
      .catch(errorHandle)
  }

  const hasRedsysChanges = useMemo(() => {
    return (
      redsysFormData.terminal !== initialRedsysFormData.terminal ||
      redsysFormData.merchantCode !== initialRedsysFormData.merchantCode ||
      redsysFormData.merchantKey !== initialRedsysFormData.merchantKey
    )
  }, [redsysFormData, initialRedsysFormData])

  const isDisabled =
    !redsysFormData.terminal ||
    !redsysFormData.merchantCode ||
    !redsysFormData.merchantKey ||
    !hasRedsysChanges

  return (
    <div className={'relative bg-white rounded-md p-8 shadow-lg transition-opacity space-y-6'}>
      <div>
        <h2 className={'text-2xl font-bold'}>{texts.title}</h2>
      </div>
      {/* toggle to show/hide form */}
      <div className="flex items-center gap-2">
        <p className="font-medium">{texts.enableToggleLabel}</p>
        <ToggleSwitch checked={isFormVisible} onChange={setIsFormVisible} />
      </div>

      {isFormVisible && (
        <>
          <div className="grid gap-6 md:grid-cols-2">
            <div>
              <NormalFormTextField
                label={texts.field1}
                name="redsysTerminal"
                value={redsysFormData.terminal}
                onChange={(value) => onRedsysFieldChange('terminal', value)}
                required
              />
            </div>
            <div>
              <NormalFormTextField
                label={texts.field2}
                name="redsysMerchantCode"
                value={redsysFormData.merchantCode}
                onChange={(value) => onRedsysFieldChange('merchantCode', value)}
                required
              />
            </div>
          </div>
          <NormalFormPasswordField
            label={texts.field3}
            name="redsysMerchantKey"
            value={redsysFormData.merchantKey}
            onChange={(value) => onRedsysFieldChange('merchantKey', value)}
            required
          />

          <Button
            onClick={onSubmitRedsysConfig}
            isLoading={redsysConfigLoading || saveRedsysConfigurationLoading}
            disabled={isDisabled}
          >
            {texts.submitButton}
          </Button>
        </>
      )}
    </div>
  )
}

/* const FormWrapper = styled.div`
  background-color: white;
  border-radius: ${({ theme }) => theme.border.radius.md};
  padding: 2rem;
  box-shadow: 0 8px 10px rgba(0, 0, 0, 0.1);
`

const CardWrapper = styled(FormWrapper)<{ $isDisabled?: boolean }>`
  position: relative;
  opacity: ${({ $isDisabled }) => ($isDisabled ? 0.6 : 1)};
  pointer-events: ${({ $isDisabled }) => ($isDisabled ? 'none' : 'auto')};
  border: ${({ theme, $isDisabled }) => ($isDisabled ? `1px dashed ${theme.colors.background2}` : 'none')};
` */

/* export const ResdysConfigurationForm = () => {
  const userLanguage = useUserLanguage()
  const texts = userLanguage === 'es' ? textsData.es : textsData.en

  const [isFormVisible, setIsFormVisible] = useState(false)

  const [redsysFormData, setRedsysFormData] = useState({
    terminal: '',
    merchantCode: '',
    merchantKey: '',
  })
  const [initialRedsysFormData, setInitialRedsysFormData] = useState({
    terminal: '',
    merchantCode: '',
    merchantKey: '',
  })

  const {data, loading: redsysConfigLoading} = useGetRedsysConfigurationQuery()

  const [saveRedsysConfiguration, { loading: saveRedsysConfigurationLoading }] = useSaveRedsysConfigurationMutation()

  const { confirmDialogModalOpen } = useOpeningElementsControllerContext()

  const { open: errorHandle } = useErrorHandlerContext()
  useEffect(() => {
  // Solo procesar cuando los datos estén cargados
  if (!data) return

  const redsysConfig = data.getRedsysConfiguration

  if (redsysConfig.__typename === 'RedsysUserConfigOutput') {
    // Asegurarse de que los valores sean strings y no otros tipos de datos
    const initialData = {
      terminal: redsysConfig.terminal,
      merchantCode: redsysConfig.merchantCode,
      merchantKey: '123456789abcdefghijk',
    }
    setRedsysFormData(initialData)
    setInitialRedsysFormData(initialData)
  } else if (redsysConfig.__typename === 'ActionResponseMessageOutput') {
    // Si no hay configuración de Redsys, inicializar con valores vacíos
    setIsFormVisible(false)
  }
}, [data, redsysConfigLoading])

const onRedsysFieldChange = (field: 'terminal' | 'merchantCode' | 'merchantKey', value: string | null) => {
  setRedsysFormData((prev) => ({
    ...prev,
    [field]: value ?? '',
  }))
}

const onSubmitRedsysConfig = () => {
  saveRedsysConfiguration({
    variables: {
      data: {
        terminal: redsysFormData.terminal,
        merchantCode: redsysFormData.merchantCode,
        merchantKey: redsysFormData.merchantKey,
      },
    },
  })
    .then(() => {
      // Actualizar los valores iniciales después de guardar exitosamente
      setInitialRedsysFormData({
        terminal: redsysFormData.terminal,
        merchantCode: redsysFormData.merchantCode,
        merchantKey: redsysFormData.merchantKey,
      })
      confirmDialogModalOpen({
        title: texts.success.title,
        description: texts.success.description,
      })
    })
    .catch(errorHandle)
}

const hasRedsysChanges = useMemo(() => {
  return (
    redsysFormData.terminal !== initialRedsysFormData.terminal ||
    redsysFormData.merchantCode !== initialRedsysFormData.merchantCode ||
    redsysFormData.merchantKey !== initialRedsysFormData.merchantKey
  )
}, [redsysFormData, initialRedsysFormData])

return (
    <CardWrapper>
      <RowSpacer height={1} />
      <Row>
        <Col>
          <NormalFormTextField
            label={texts.field1}
            name="redsysTerminal"
            value={redsysFormData.terminal}
            onChange={(value) => onRedsysFieldChange('terminal', value)}
            required
          />
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <NormalFormTextField
            label={texts.field2}
            name="redsysMerchantCode"
            value={redsysFormData.merchantCode}
            onChange={(value) => onRedsysFieldChange('merchantCode', value)}
            required
          />
        </Col>
        <Col md={6}>
          <NormalFormPasswordField
            label={texts.field3}
            name="redsysMerchantKey"
            value={redsysFormData.merchantKey}
            onChange={(value) => onRedsysFieldChange('merchantKey', value)}
            required
          />
        </Col>
      </Row>
      <RowSpacer height={2} />
      <Row>
        <Col>
          <Button
            onClick={onSubmitRedsysConfig}
            isLoading={redsysConfigLoading || saveRedsysConfigurationLoading}
            disabled={
              !redsysFormData.terminal ||
              !redsysFormData.merchantCode ||
              !redsysFormData.merchantKey ||
              !hasRedsysChanges
            }
          >
            {texts.submitButton}
          </Button>
        </Col>
      </Row>
    </CardWrapper>
  )

} */

